import Vue from 'vue'
import store from 'store'
import { mapActions } from 'vuex'
import RedesignMegaNav from './mega-nav'

export default (el) =>
  new Vue({
    el,
    store,
    data () {
      return {
        isCondensed: false,
        stickyBehavior: 'sticky',
        lastScrollY: 0,
        wishlistCount: 0,
        swymListeners: [],
        swymTracker: null,
        megaNav: null
      }
    },
    computed: {
      cartItemCount () {
        return Number(this.$store.state.global.cart?.item_count) || 0
      }
    },
    mounted () {
      this.stickyBehavior = this.$el.dataset.stickyBehavior || 'sticky'
      this.onScroll = this.onScroll.bind(this)
      this.updateMeasurements = this.updateMeasurements.bind(this)
      this.applyStickyMode = this.applyStickyMode.bind(this)
      this.updateStickyOffset = this.updateStickyOffset.bind(this)
      this.lastScrollY = this.getScrollTop()
      this.applyStickyMode()
      this.onScroll()
      this.updateMeasurements()
      this.initWishlistCounter()
      this.initMegaNav()
      window.addEventListener('scroll', this.onScroll, { passive: true })
      document.addEventListener('scroll', this.onScroll, { passive: true })
      document.body.addEventListener('scroll', this.onScroll, { passive: true })
      window.addEventListener('resize', this.updateMeasurements)
      window.addEventListener('load', this.updateMeasurements)

      const logoImage = this.$el.querySelector('.redesign-header__logo-image')

      if (logoImage) {
        logoImage.addEventListener('load', this.updateMeasurements)
      }
    },
    beforeDestroy () {
      window.removeEventListener('scroll', this.onScroll)
      document.removeEventListener('scroll', this.onScroll)
      document.body.removeEventListener('scroll', this.onScroll)
      window.removeEventListener('resize', this.updateMeasurements)
      window.removeEventListener('load', this.updateMeasurements)

      const logoImage = this.$el.querySelector('.redesign-header__logo-image')

      if (logoImage) {
        logoImage.removeEventListener('load', this.updateMeasurements)
      }

      document.body.classList.remove('redesign-header-condensed')
      this.destroyMegaNav()
      this.cleanupWishlistCounter()
    },
    methods: {
      updateStickyOffset () {
        const barHeight = this.$el.querySelector('.redesign-header__bar')?.offsetHeight || 0

        document.documentElement.style.setProperty(
          '--redesign-header-current-height',
          `${barHeight}px`
        )
      },
      ...mapActions('cart', ['toggleCart']),
      ...mapActions('global', ['toggleSearch']),
      getScrollTop () {
        return Math.max(
          window.scrollY || 0,
          document.documentElement.scrollTop || 0,
          document.body.scrollTop || 0
        )
      },
      applyStickyMode () {
        this.$el.classList.toggle('is-condensed', false)
        this.$el.classList.remove('is-nav-hidden')
        document.body.classList.remove('redesign-header-condensed')
        document.body.classList.toggle(
          'redesign-header-static-mode',
          this.stickyBehavior === 'static'
        )

        if (this.stickyBehavior !== 'hide-on-scroll') {
          this.$el.classList.remove('is-hidden')
          this.$el.style.removeProperty('transform')
        }

        if (this.stickyBehavior !== 'sticky') {
          this.$el.classList.remove('is-condensed')
        }
      },
      onSearchClick () {
        document.dispatchEvent(new window.CustomEvent('redesign:overlay-open', {
          detail: { source: 'search' }
        }))
        this.toggleSearch()
      },
      onCartClick () {
        document.dispatchEvent(new window.CustomEvent('redesign:overlay-open', {
          detail: { source: 'cart' }
        }))
        this.toggleCart()
      },
      initMegaNav () {
        const megaNavElement = this.$el.querySelector('[data-redesign-mega-nav]')

        if (!megaNavElement) return

        this.megaNav = new RedesignMegaNav(megaNavElement)
        this.megaNav.init()
      },
      destroyMegaNav () {
        if (!this.megaNav) return

        this.megaNav.destroy()
        this.megaNav = null
      },
      cleanupWishlistCounter () {
        this.swymListeners.forEach(({ evtLayer, eventName, handler }) => {
          if (!evtLayer || !eventName || !handler) return

          evtLayer.removeEventListener(eventName, handler)
        })

        this.swymListeners = []
        this.swymTracker = null
      },
      initWishlistCounter () {
        const syncWishlistCount = (swat) => {
          if (!swat || typeof swat.fetchWishlistCount !== 'function') return

          swat.fetchWishlistCount((response) => {
            const responseCount = response && response.data && typeof response.data.count !== 'undefined'
              ? response.data.count
              : response && typeof response.count !== 'undefined'
                ? response.count
                : 0
            const count = Number(responseCount) || 0

            this.wishlistCount = count
            this.$el.querySelectorAll('[data-wishlist-count]').forEach((counter) => {
              counter.textContent = count
              counter.hidden = count <= 0
            })
          })
        }

        const bindWishlistEvents = (swat) => {
          if (this.swymTracker === swat) {
            syncWishlistCount(swat)
            return
          }

          this.cleanupWishlistCounter()
          this.swymTracker = swat
          syncWishlistCount(swat)

          if (!swat || !swat.evtLayer || !swat.JSEvents) return

          const eventNames = [
            swat.JSEvents.configLoaded,
            swat.JSEvents.sessionStarted,
            swat.JSEvents.sessionCreated,
            swat.JSEvents.customerInfoRefreshed,
            swat.JSEvents.regidRefreshed,
            swat.JSEvents.addedToWishlist,
            swat.JSEvents.removedFromWishlist
          ].filter(Boolean)

          eventNames.forEach((eventName) => {
            const handler = () => syncWishlistCount(swat)

            swat.evtLayer.addEventListener(eventName, handler)
            this.swymListeners.push({ evtLayer: swat.evtLayer, eventName, handler })
          })
        }

        window.SwymCallbacks = window.SwymCallbacks || []
        window.SwymCallbacks.push(bindWishlistEvents)

        if (window._swat) {
          bindWishlistEvents(window._swat)
        }
      },
      updateMeasurements () {
        const branding = this.$el.querySelector('.redesign-header__branding')
        const navRow = this.$el.querySelector('.redesign-header__nav-row')

        if (!branding || !navRow) return

        const brandingHeight = branding.offsetHeight
        const navHeight = navRow.offsetHeight
        // 1.2rem gap, scales with the fluid root font-size above 1512px
        const remBase = parseFloat(window.getComputedStyle(document.documentElement).fontSize) || 10
        const navOffset = brandingHeight + 1.2 * remBase

        this.$el.style.setProperty(
          '--redesign-branding-height',
          ''.concat(brandingHeight, 'px')
        )
        this.$el.style.setProperty(
          '--redesign-nav-offset',
          ''.concat(navOffset, 'px')
        )
        this.$el.style.setProperty(
          '--redesign-center-height-expanded',
          ''.concat(navOffset + navHeight, 'px')
        )
        this.$el.style.setProperty(
          '--redesign-center-height-condensed',
          ''.concat(navHeight, 'px')
        )

        document.documentElement.style.setProperty(
          '--redesign-center-height-expanded',
          ''.concat(navOffset + navHeight, 'px')
        )
        document.documentElement.style.setProperty(
          '--redesign-center-height-condensed',
          ''.concat(navHeight, 'px')
        )

        this.updateStickyOffset()
      },
      onScroll () {
        const currentScrollY = this.getScrollTop()
        const isScrollingDown = currentScrollY > this.lastScrollY

        // Desktop mega nav removes the scrollbar while open. Freeze the desktop
        // header state so the resulting scroll event cannot close the hovered nav.
        // Mobile keeps the original scroll handling so its fixed offset stays in sync.
        if (
          window.matchMedia('(min-width: 1280px)').matches &&
          document.body.classList.contains('redesign-menu-open')
        ) {
          this.lastScrollY = currentScrollY
          return
        }

        if (this.stickyBehavior === 'static') {
          this.isCondensed = false
          this.$el.classList.remove('is-condensed')
          this.$el.classList.remove('is-nav-hidden')
          this.$el.classList.remove('is-hidden')
          this.$el.style.removeProperty('transform')
          document.body.classList.remove('redesign-header-condensed')
          this.updateStickyOffset()

          this.lastScrollY = currentScrollY
          return
        }

        if (this.stickyBehavior === 'hide-on-scroll') {
          this.isCondensed = false
          this.$el.classList.remove('is-condensed')
          this.$el.classList.remove('is-nav-hidden')
          document.body.classList.remove('redesign-header-condensed')

          const shouldHide = currentScrollY > 24 && isScrollingDown

          this.$el.classList.toggle('is-hidden', shouldHide)
          this.$el.style.transform = shouldHide
            ? 'translateY(calc(-100% - var(--redesign-announcement-height, 0px)))'
            : ''
          this.updateStickyOffset()

          this.lastScrollY = currentScrollY
          return
        }

        const atTop = currentScrollY <= 24
        const delta = currentScrollY - this.lastScrollY
        this.lastScrollY = currentScrollY

        // Announcement bar + header shift-up: only at the very top (threshold)
        document.body.classList.toggle('redesign-header-condensed', !atTop)

        this.isCondensed = false
        this.$el.classList.remove('is-condensed')

        // Desktop menu (nav row): hide on scroll down, reveal on scroll up / at top.
        // The 5px threshold ignores momentum-settle / jitter so it doesn't flicker.
        if (atTop) {
          this.$el.classList.remove('is-nav-hidden')
        } else if (delta > 5) {
          this.$el.classList.add('is-nav-hidden')
        } else if (delta < -5) {
          this.$el.classList.remove('is-nav-hidden')
        }

        this.updateStickyOffset()
      }
    }
  })
